import React, { useState, useEffect } from 'react'
import { motion } from 'framer-motion'
import * as FiIcons from 'react-icons/fi'
import SafeIcon from '../../common/SafeIcon'
import { supabase } from '../../lib/supabase'
import Toast from '../ui/Toast'

const { FiSettings, FiSave, FiEye, FiEyeOff, FiDollarSign, FiType, FiEdit3, FiDatabase, FiImage, FiCalendar } = FiIcons

const MenuConfig = () => {
  const [config, setConfig] = useState({
    showMenuLegend: true,
    menuPrice: 13.00,
    menuTitle: 'MENÚ DEL DÍA',
    menuDescription: 'Incluye primer plato, segundo plato, pan y postre',
    showFestiveBanner: false,
    festiveImageUrl: '',
    festiveAltText: 'Imagen festiva',
    festiveEndDate: ''
  })
  
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [toast, setToast] = useState(null)
  const [tableExists, setTableExists] = useState(false)
  const [creatingTable, setCreatingTable] = useState(false)

  useEffect(() => {
    checkTableExists()
  }, [])

  const showToast = (message, type = 'success') => {
    setToast({ message, type })
    setTimeout(() => setToast(null), 3000)
  }

  const checkTableExists = async () => {
    try {
      setLoading(true)
      // Try to query the table to see if it exists
      const { data, error } = await supabase
        .from('menu_config')
        .select('*')
        .limit(1)

      if (error) {
        if (error.code === '42P01' || error.message.includes('relation "public.menu_config" does not exist')) {
          // Table doesn't exist
          setTableExists(false)
        } else {
          console.error('Error checking table:', error)
          showToast('Error al verificar la configuración', 'error')
        }
      } else {
        setTableExists(true)
        await fetchConfig()
      }
    } catch (error) {
      console.error('Error checking table exists:', error)
      setTableExists(false)
    } finally {
      setLoading(false)
    }
  }

  const createTable = async () => {
    try {
      setCreatingTable(true)
      showToast('La tabla ya ha sido creada en la base de datos')
      setTableExists(true)
      await fetchConfig()
    } catch (error) {
      console.error('Error creating table:', error)
      showToast('Error al verificar la tabla de configuración', 'error')
    } finally {
      setCreatingTable(false)
    }
  }

  const fetchConfig = async () => {
    try {
      const { data, error } = await supabase
        .from('menu_config')
        .select('*')
        .single()

      if (error) {
        if (error.code === 'PGRST116') {
          // No config exists, create default
          const { data: newConfig, error: insertError } = await supabase
            .from('menu_config')
            .insert([{
              show_menu_legend: true,
              menu_price: 13.00,
              menu_title: 'MENÚ DEL DÍA',
              menu_description: 'Incluye primer plato, segundo plato, pan y postre',
              show_festive_banner: false,
              festive_image_url: null,
              festive_alt_text: 'Imagen festiva',
              festive_end_date: null
            }])
            .select()
            .single()

          if (insertError) {
            console.error('Error creating config:', insertError)
            showToast('Error al crear configuración por defecto', 'error')
          } else {
            setConfig({
              showMenuLegend: newConfig.show_menu_legend,
              menuPrice: newConfig.menu_price,
              menuTitle: newConfig.menu_title || 'MENÚ DEL DÍA',
              menuDescription: newConfig.menu_description || 'Incluye primer plato, segundo plato, pan y postre',
              showFestiveBanner: newConfig.show_festive_banner || false,
              festiveImageUrl: newConfig.festive_image_url || '',
              festiveAltText: newConfig.festive_alt_text || 'Imagen festiva',
              festiveEndDate: newConfig.festive_end_date || ''
            })
          }
        } else {
          throw error
        }
      } else {
        setConfig({
          showMenuLegend: data.show_menu_legend,
          menuPrice: data.menu_price,
          menuTitle: data.menu_title || 'MENÚ DEL DÍA',
          menuDescription: data.menu_description || 'Incluye primer plato, segundo plato, pan y postre',
          showFestiveBanner: data.show_festive_banner || false,
          festiveImageUrl: data.festive_image_url || '',
          festiveAltText: data.festive_alt_text || 'Imagen festiva',
          festiveEndDate: data.festive_end_date || ''
        })
      }
    } catch (error) {
      console.error('Error fetching config:', error)
      showToast('Error al cargar configuración', 'error')
    }
  }

  const handleSave = async () => {
    try {
      setSaving(true)
      
      // First try to get the existing config to update it
      const { data: existingConfig } = await supabase
        .from('menu_config')
        .select('id')
        .single()

      if (existingConfig) {
        // Update existing config
        const { error } = await supabase
          .from('menu_config')
          .update({
            show_menu_legend: config.showMenuLegend,
            menu_price: config.menuPrice,
            menu_title: config.menuTitle,
            menu_description: config.menuDescription,
            show_festive_banner: config.showFestiveBanner,
            festive_image_url: config.festiveImageUrl || null,
            festive_alt_text: config.festiveAltText,
            festive_end_date: config.festiveEndDate || null,
            updated_at: new Date().toISOString()
          })
          .eq('id', existingConfig.id)
          
        if (error) {
          // If error is about missing column, notify user
          if (error.message.includes('festive_end_date')) {
             throw new Error('La columna festive_end_date no existe. Por favor, ejecuta la migración SQL en Supabase.');
          }
          throw error
        }
      } else {
        // Insert new config
        const { error } = await supabase
          .from('menu_config')
          .insert([{
            show_menu_legend: config.showMenuLegend,
            menu_price: config.menuPrice,
            menu_title: config.menuTitle,
            menu_description: config.menuDescription,
            show_festive_banner: config.showFestiveBanner,
            festive_image_url: config.festiveImageUrl || null,
            festive_alt_text: config.festiveAltText,
            festive_end_date: config.festiveEndDate || null
          }])
          
        if (error) throw error
      }

      showToast('Configuración guardada correctamente')
    } catch (error) {
      console.error('Error saving config:', error)
      showToast('Error al guardar configuración: ' + error.message, 'error')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="flex justify-center py-8">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-vip-orange"></div>
      </div>
    )
  }

  if (!tableExists) {
    return (
      <div className="space-y-6">
        {/* Toast */}
        {toast && (
          <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
        )}

        {/* Table Creation Required */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-yellow-50 border border-yellow-200 rounded-lg p-6"
        >
          <div className="flex items-center mb-4">
            <SafeIcon icon={FiDatabase} className="text-yellow-600 mr-3 text-xl" />
            <h2 className="text-lg font-semibold text-yellow-900">Tabla de Configuración Requerida</h2>
          </div>
          <p className="text-yellow-800 mb-4">
            La tabla de configuración del menú no existe en la base de datos. Es necesario crearla para poder guardar la configuración personalizada.
          </p>
          
          <div className="bg-white rounded-lg p-4 mb-4 border border-yellow-300">
            <h3 className="font-semibold text-gray-900 mb-2">SQL para crear la tabla manualmente:</h3>
            <pre className="text-sm bg-gray-100 p-3 rounded overflow-x-auto">
{`-- Crear tabla menu_config
CREATE TABLE menu_config (
  id SERIAL PRIMARY KEY,
  show_menu_legend BOOLEAN DEFAULT true,
  menu_price DECIMAL(10,2) DEFAULT 13.00,
  menu_title VARCHAR(255) DEFAULT 'MENÚ DEL DÍA',
  menu_description TEXT DEFAULT 'Incluye primer plato, segundo plato, pan y postre',
  show_festive_banner BOOLEAN DEFAULT false,
  festive_image_url TEXT,
  festive_alt_text TEXT DEFAULT 'Imagen festiva',
  festive_end_date DATE,
  created_at TIMESTAMP WITH TIME ZONE DEFAULT NOW(),
  updated_at TIMESTAMP WITH TIME ZONE DEFAULT NOW()
);

-- Habilitar RLS
ALTER TABLE menu_config ENABLE ROW LEVEL SECURITY;

-- Política para lectura pública
CREATE POLICY "Public can read menu config" ON menu_config FOR SELECT USING (true);

-- Política para gestión por usuarios autenticados
CREATE POLICY "Authenticated users can manage menu config" ON menu_config FOR ALL USING (true);

-- Insertar configuración por defecto
INSERT INTO menu_config (
  show_menu_legend, menu_price, menu_title, menu_description, show_festive_banner, festive_image_url, festive_alt_text, festive_end_date
) VALUES (
  true, 13.00, 'MENÚ DEL DÍA', 'Incluye primer plato, segundo plato, pan y postre', false, null, 'Imagen festiva', null
);`}
            </pre>
          </div>

          <div className="flex gap-3">
            <button
              onClick={createTable}
              disabled={creatingTable}
              className="flex items-center px-4 py-2 bg-vip-orange text-white rounded-md hover:bg-vip-dark transition-colors duration-200 disabled:opacity-50"
            >
              {creatingTable ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Verificando tabla...
                </>
              ) : (
                <>
                  <SafeIcon icon={FiDatabase} className="mr-2" />
                  Verificar Tabla
                </>
              )}
            </button>
            <button
              onClick={checkTableExists}
              className="flex items-center px-4 py-2 bg-gray-500 text-white rounded-md hover:bg-gray-600 transition-colors duration-200"
            >
              Verificar de Nuevo
            </button>
          </div>
        </motion.div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Toast */}
      {toast && (
        <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />
      )}

      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-vip-orange">Configuración del Menú</h1>
        <p className="text-vip-brown">Personaliza la apariencia y contenido del menú público</p>
      </div>

      {/* Configuration Form */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-lg shadow-md p-6"
      >
        <div className="flex items-center mb-6">
          <SafeIcon icon={FiSettings} className="text-vip-orange mr-3 text-xl" />
          <h2 className="text-lg font-semibold text-vip-brown">Configuración General</h2>
        </div>

        <div className="space-y-6">
          {/* Festive Banner Section */}
          <div className="p-4 border-2 border-orange-200 rounded-lg bg-orange-50">
            <h3 className="font-semibold text-orange-900 mb-4 flex items-center">
              <SafeIcon icon={FiImage} className="mr-2" />
              Banner Festivo (Días Especiales)
            </h3>
            
            {/* Festive Banner Toggle */}
            <div className="flex items-center justify-between p-3 border border-orange-200 rounded-lg mb-4 bg-white">
              <div>
                <h4 className="font-medium text-gray-900">Mostrar Banner Festivo</h4>
                <p className="text-sm text-gray-600 mt-1">
                  Muestra una imagen especial para días festivos arriba del menú del día
                </p>
              </div>
              <button
                onClick={() => setConfig(prev => ({ ...prev, showFestiveBanner: !prev.showFestiveBanner }))}
                className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                  config.showFestiveBanner ? 'bg-orange-500' : 'bg-gray-300'
                }`}
              >
                <span
                  className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                    config.showFestiveBanner ? 'translate-x-6' : 'translate-x-1'
                  }`}
                />
              </button>
            </div>

            {/* Festive Image URL */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                URL de la Imagen Festiva
              </label>
              <input
                type="url"
                value={config.festiveImageUrl}
                onChange={(e) => setConfig(prev => ({ ...prev, festiveImageUrl: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="https://ejemplo.com/imagen-navidad.jpg"
                disabled={!config.showFestiveBanner}
              />
              <p className="text-sm text-gray-600 mt-1">
                URL externa de la imagen que se mostrará en días festivos
              </p>
            </div>

            {/* Festive Alt Text */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                Texto Alternativo
              </label>
              <input
                type="text"
                value={config.festiveAltText}
                onChange={(e) => setConfig(prev => ({ ...prev, festiveAltText: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                placeholder="Imagen festiva"
                disabled={!config.showFestiveBanner}
              />
              <p className="text-sm text-gray-600 mt-1">
                Descripción de la imagen para accesibilidad
              </p>
            </div>

            {/* Festive End Date */}
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-900 mb-2">
                <SafeIcon icon={FiCalendar} className="inline mr-2" />
                Mostrar hasta la fecha (Opcional)
              </label>
              <input
                type="date"
                value={config.festiveEndDate}
                onChange={(e) => setConfig(prev => ({ ...prev, festiveEndDate: e.target.value }))}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-orange-500"
                disabled={!config.showFestiveBanner}
              />
              <p className="text-sm text-gray-600 mt-1">
                Si estableces una fecha, el banner se ocultará automáticamente al día siguiente de la misma. Déjalo vacío para mantenerlo indefinidamente.
              </p>
            </div>

            {/* Preview Festive Banner */}
            {config.showFestiveBanner && config.festiveImageUrl && (
              <div className="p-3 bg-white border border-orange-200 rounded-lg">
                <h5 className="font-medium text-gray-900 mb-2">Vista Previa:</h5>
                <div className="bg-white rounded-lg p-4 text-center border border-gray-200">
                  <img 
                    src={config.festiveImageUrl} 
                    alt={config.festiveAltText} 
                    className="max-h-32 mx-auto rounded-lg shadow-md"
                    onError={(e) => {
                      e.target.style.display = 'none'
                      e.target.nextSibling.style.display = 'block'
                    }}
                  />
                  <div className="text-gray-500 text-sm mt-2" style={{display: 'none'}}>
                    Error al cargar la imagen
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Menu Legend Toggle */}
          <div className="flex items-center justify-between p-4 border border-gray-200 rounded-lg">
            <div>
              <h3 className="font-medium text-gray-900">Mostrar Leyenda del Menú</h3>
              <p className="text-sm text-gray-600 mt-1">
                Muestra una sección destacada con la información del menú del día
              </p>
            </div>
            <button
              onClick={() => setConfig(prev => ({ ...prev, showMenuLegend: !prev.showMenuLegend }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors duration-200 ${
                config.showMenuLegend ? 'bg-vip-orange' : 'bg-gray-300'
              }`}
            >
              <span
                className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform duration-200 ${
                  config.showMenuLegend ? 'translate-x-6' : 'translate-x-1'
                }`}
              />
            </button>
          </div>

          {/* Menu Title */}
          <div className="p-4 border border-gray-200 rounded-lg">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              <SafeIcon icon={FiType} className="inline mr-2" />
              Título del Menú
            </label>
            <input
              type="text"
              value={config.menuTitle}
              onChange={(e) => setConfig(prev => ({ ...prev, menuTitle: e.target.value }))}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vip-orange"
              placeholder="MENÚ DEL DÍA"
            />
            <p className="text-sm text-gray-600 mt-1">
              Este título aparecerá en la leyenda del menú
            </p>
          </div>

          {/* Menu Price */}
          <div className="p-4 border border-gray-200 rounded-lg">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              <SafeIcon icon={FiDollarSign} className="inline mr-2" />
              Precio del Menú (€)
            </label>
            <input
              type="number"
              step="0.01"
              min="0"
              value={config.menuPrice}
              onChange={(e) => setConfig(prev => ({ ...prev, menuPrice: parseFloat(e.target.value) || 0 }))}
              className="w-full md:w-32 px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vip-orange"
              placeholder="13.00"
            />
            <p className="text-sm text-gray-600 mt-1">
              Este precio se mostrará en la leyenda del menú
            </p>
          </div>

          {/* Menu Description */}
          <div className="p-4 border border-gray-200 rounded-lg">
            <label className="block text-sm font-medium text-gray-900 mb-2">
              <SafeIcon icon={FiEdit3} className="inline mr-2" />
              Descripción del Menú
            </label>
            <textarea
              value={config.menuDescription}
              onChange={(e) => setConfig(prev => ({ ...prev, menuDescription: e.target.value }))}
              rows={3}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-vip-orange"
              placeholder="Incluye primer plato, segundo plato, pan y postre"
            />
            <p className="text-sm text-gray-600 mt-1">
              Esta descripción aparecerá debajo del precio en la leyenda
            </p>
          </div>

          {/* Save Button */}
          <div className="flex justify-end">
            <button
              onClick={handleSave}
              disabled={saving}
              className="flex items-center px-6 py-2 bg-vip-orange text-white rounded-md hover:bg-vip-dark transition-colors duration-200 disabled:opacity-50"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <SafeIcon icon={FiSave} className="mr-2" />
                  Guardar Configuración
                </>
              )}
            </button>
          </div>
        </div>
      </motion.div>

      {/* Information */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.1 }}
        className="bg-blue-50 border border-blue-200 rounded-lg p-6"
      >
        <h4 className="font-semibold text-blue-900 mb-3">Información del Sistema</h4>
        <ul className="space-y-2 text-blue-800 text-sm">
          <li className="flex items-start">
            <span className="inline-block w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            El banner festivo se muestra arriba de la leyenda del menú cuando está activado. Si tiene fecha de expiración, se ocultará automáticamente al pasar ese día.
          </li>
          <li className="flex items-start">
            <span className="inline-block w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            La leyenda del menú del día se muestra de lunes a viernes
          </li>
          <li className="flex items-start">
            <span className="inline-block w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Los títulos de categorías "Primeros Platos" y "Segundos Platos" siempre se muestran
          </li>
          <li className="flex items-start">
            <span className="inline-block w-2 h-2 bg-blue-400 rounded-full mt-2 mr-3 flex-shrink-0"></span>
            Las categorías típico y parrilla se muestran tanto en días de semana como fines de semana
          </li>
        </ul>
      </motion.div>
    </div>
  )
}

export default MenuConfig